import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthShell, Field, FormError, FormNotice, SubmitButton } from "@/components/AuthShell";
import { Input, Spinner } from "@/components/ui";
import { apiError, endpoints } from "@/lib/api";

export function VerifyEmail() {
  const [params] = useSearchParams();
  const [state, setState] = React.useState({ status: "loading", msg: "" });
  const ran = React.useRef(false);

  React.useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    endpoints.verifyEmail(params.get("token") || "")
      .then(() => setState({ status: "ok", msg: "Email verified. You can now sign in." }))
      .catch((e) => setState({ status: "error", msg: apiError(e) }));
  }, [params]);

  return (
    <AuthShell testid="verify-email-page" eyebrow="Email verification" title="Verifying your email"
      footer={<Link to="/login" className="text-gold hover:underline" data-testid="verify-to-login">Go to sign in</Link>}>
      {state.status === "loading" && <div className="flex items-center gap-2 text-white/60 text-sm"><Spinner size={14} /> Checking link…</div>}
      {state.status === "ok" && <FormNotice msg={state.msg} />}
      {state.status === "error" && <FormError msg={state.msg} />}
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = React.useState("");
  const [sent, setSent] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try { await endpoints.forgotPassword(email); setSent(true); } catch (ex) { setErr(apiError(ex)); } finally { setLoading(false); }
  };
  return (
    <AuthShell testid="forgot-password-page" eyebrow="Password reset" title="Forgot your password?"
      subtitle="Enter your email and we'll send you a secure reset link."
      footer={<Link to="/login" className="text-gold hover:underline" data-testid="forgot-to-login">Back to sign in</Link>}>
      <form onSubmit={submit} data-testid="forgot-form">
        <FormError msg={err} />
        {sent ? <FormNotice msg="If an account exists for this email, a reset link has been sent. It expires in 1 hour." /> : (
          <>
            <Field label="Email"><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" /></Field>
            <SubmitButton loading={loading} testid="forgot-submit">Send reset link</SubmitButton>
          </>
        )}
      </form>
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = React.useState("");
  const [pw2, setPw2] = React.useState("");
  const [err, setErr] = React.useState("");
  const [ok, setOk] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) return setErr("Password must be at least 8 characters.");
    if (pw !== pw2) return setErr("Passwords do not match.");
    setLoading(true);
    try {
      await endpoints.resetPassword(params.get("token") || "", pw);
      setOk(true);
      setTimeout(() => nav("/login"), 1800);
    } catch (ex) { setErr(apiError(ex)); } finally { setLoading(false); }
  };
  return (
    <AuthShell testid="reset-password-page" eyebrow="Set password" title="Choose a new password"
      footer={<Link to="/login" className="text-gold hover:underline" data-testid="reset-to-login">Back to sign in</Link>}>
      <form onSubmit={submit} data-testid="reset-form">
        <FormError msg={err} />
        {ok ? <FormNotice msg="Password updated. Redirecting to sign in…" /> : (
          <>
            <Field label="New password"><Input type="password" required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password" /></Field>
            <Field label="Confirm password"><Input type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-confirm-password" /></Field>
            <SubmitButton loading={loading} testid="reset-submit">Update password</SubmitButton>
          </>
        )}
      </form>
    </AuthShell>
  );
}
